import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import type { Job } from '../../../core/types/data-models';
import {
  JOB_TYPE_LABEL,
  formatGenerationProgress,
  formatGenerationProgressCompact,
  formatStarted,
} from './jobDisplay';
import { useJobFlavorMessage } from './useJobFlavorMessage';

// A job shown here is either still running, or a COMPLETED
// GENERATE_DOCUMENT_QUESTIONS job still waiting on the user's answers — the
// latter is clickable and reopens the questions dialog, which is what lets a
// fresh page load recover a flow that was interrupted by navigation.
export function JobChip({ job, docTypeName, onClick }: { job: Job; docTypeName: string | null; onClick?: () => void }) {
  const label = JOB_TYPE_LABEL[job.jobType] ?? job.jobType.replace(/_/g, ' ');
  const displayLabel = docTypeName ? `${label} · ${docTypeName}` : label;
  const isPending = job.status === 'PENDING';
  const isAwaitingAnswers = job.status === 'COMPLETED';
  const isProcessing = job.status === 'PROCESSING';
  const generationProgress = formatGenerationProgress(job.progressAdditionalData);
  const generationProgressCompact = formatGenerationProgressCompact(job.progressAdditionalData);
  const flavorMessage = useJobFlavorMessage(job.jobType, docTypeName, isProcessing);

  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ py: 0.25 }}>
          <Typography variant="caption" fontWeight={700} sx={{ display: 'block' }}>
            {isAwaitingAnswers ? 'Questions ready' : displayLabel}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            {isAwaitingAnswers ? 'Click to answer' : `${job.status} · ${job.progressPercent}%`}
          </Typography>
          {!isAwaitingAnswers && (
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
              Started {formatStarted(job.createdAt)}
            </Typography>
          )}
          {job.progressMessage && !isAwaitingAnswers && (
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
              {job.progressMessage}
            </Typography>
          )}
          {generationProgress && !isAwaitingAnswers && (
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
              {generationProgress}
            </Typography>
          )}
          {flavorMessage && (
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.85, fontStyle: 'italic' }}>
              {flavorMessage}
            </Typography>
          )}
        </Box>
      }
    >
      <Box
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        sx={{
          display: 'inline-flex',
          flexDirection: 'column',
          gap: 0.125,
          pl: 0.75,
          pr: 1.25,
          py: 0.5,
          maxWidth: 260,
          borderRadius: 3,
          bgcolor: isAwaitingAnswers ? (theme) => alpha(theme.palette.primary.main, 0.12) : 'action.hover',
          cursor: onClick ? 'pointer' : 'default',
          '&:focus-visible': onClick
            ? { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 }
            : undefined,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          {isAwaitingAnswers ? (
            <ChatBubbleOutlineIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
          ) : (
            <CircularProgress
              size={16}
              thickness={5}
              sx={{ color: isPending ? 'text.disabled' : 'primary.main', flexShrink: 0 }}
            />
          )}
          <Typography variant="caption" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
            {displayLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {isAwaitingAnswers
              ? 'Answer'
              : isPending
                ? 'Queued'
                : generationProgressCompact
                  ? `${job.progressPercent}% · ${generationProgressCompact}`
                  : `${job.progressPercent}%`}
          </Typography>
        </Box>
        {flavorMessage && (
          <Typography
            variant="caption"
            noWrap
            sx={{ pl: 2.75, fontSize: 10, lineHeight: 1.4, color: 'text.secondary', fontStyle: 'italic', opacity: 0.85 }}
          >
            {flavorMessage}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}
